import { useCallback, useEffect, useRef, useState } from "react";
import { Search } from "react-feather";
import { IoCheckmarkCircleOutline } from "react-icons/io5";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  searchBatchChargeConfirmation,
  searchChargeConfirmation,
} from "../../api/finance/chargeConfirmation";
import BatchChargeSearch from "../../components/finance/ChargeConfirmation/BatchChargeSearch";
import ChargeConfirmationSearch from "../../components/finance/ChargeConfirmation/ChargeConfirmationSearch";
import CustomerTransporterChargeApproval from "../../components/finance/ChargeConfirmation/CustomerTransporterChargeApproval";
import Grid from "../../components/Grid";
import InfoList from "../../components/InfoList";
import SideDrawer from "../../components/input/SideDrawer";
import LoadingSpinner from "../../components/LoadingSpinner";
import {
  EVInvCellStyle,
  ResizableScrollingTextRenderer,
} from "../../components/renderer/StyleRenderer";
import Tab from "../../components/Tab";
import { FORMS, MODULES } from "../../const";
import useUserCompany from "../../hooks/useUserCompany";

const searchTypeConst = {
  JOB: "JOB",
  BATCH: "BATCH",
};

const HEADER_FORM = FORMS.CHARGE_APPROVAL.HEADER.form;

const ChargeConfirmation = () => {
  const auth = useSelector((state) => state.user.permissions[MODULES.Finance]);
  const chargeApprovalHeaderPermissions = auth[HEADER_FORM];
  const evPermissions = chargeApprovalHeaderPermissions?.FORM?.EV_APPROVE;
  const invPermissions = chargeApprovalHeaderPermissions?.FORM?.INV_APPROVE;
  const invStatusPermission = chargeApprovalHeaderPermissions?.invStatus?.VIEW;
  const evStatusPermission = chargeApprovalHeaderPermissions?.evStatus?.VIEW;

  const userCompany = useUserCompany();

  const userCurrencyCode = userCompany ? userCompany.comCurrencyCode : null;

  const pendingSearchPermission =
    chargeApprovalHeaderPermissions?.FORM?.PENDING_SEARCH;
  const approvedSearchPermission =
    chargeApprovalHeaderPermissions?.FORM?.APPROVED_SEARCH;

  const formatter = Intl.NumberFormat("en-US", {
    style: "currency",
    currency: userCurrencyCode,
  });

  const gridRef = useRef();
  const batchGridRef = useRef();

  const [tab, setTab] = useState(
    pendingSearchPermission ? 1 : approvedSearchPermission ? 2 : -1
  );
  const [jobs, setJobs] = useState([]);
  const [batches, setBatches] = useState([]);
  const [searchClicked, setSearchClicked] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [stkType, setStkType] = useState(null);
  const [currentBatchId, setCurrentBatchId] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [activeAccordion1, setActiveAccordion1] = useState(true);
  const [activeAccordion2, setActiveAccordion2] = useState(true);
  const [searchParams, setSearchParams] = useState({});
  const [batchSearchParams, setBatchSearchParams] = useState({});
  const [currentSearchType, setCurrentSearchType] = useState(null);
  const [updateNeeded, setUpdateNeeded] = useState(false);
  const [isBatchSearch, setIsBatchSearch] = useState(false);
  const [jobSummary, setJobSummary] = useState([]);
  const [jobCount, setJobCount] = useState(0);

  const handleSearch = useCallback(async (data) => {
    setIsBatchSearch(false);
    setSearchClicked(true);
    setIsLoading(true);
    setSearchParams(data);
    setCurrentSearchType(searchTypeConst.JOB);
    const result = await searchChargeConfirmation(data);
    setJobs(result?.searchResult);
    setJobCount(result?.jobCount ?? 0);
    const currentJobSummary = result?.jobSummaryResponse;
    const jobInformation = [
      {
        title: "Completed Jobs",
        value: currentJobSummary?.jsCompletedJobs
          ? `${currentJobSummary?.jsCompletedJobs} Jobs`
          : "0 Jobs",
      },
      {
        title: "On Time Placement",
        value: currentJobSummary?.jsOnTimePlacement
          ? `${currentJobSummary?.jsOnTimePlacement} %`
          : "0 %",
      },
      {
        title: "On Time Delivery",
        value: currentJobSummary?.jsOnTimeDelivery
          ? `${currentJobSummary?.jsOnTimeDelivery} %`
          : "0 %",
      },
      {
        title: "Late Booking",
        value: currentJobSummary?.jsLateBooking
          ? `${currentJobSummary?.jsLateBooking} %`
          : "0 %",
      },
      {
        title: "Same Day Delivery",
        value: currentJobSummary?.jsSameDayDelivery
          ? `${currentJobSummary?.jsSameDayDelivery} %`
          : "0 %",
      },
      {
        title: "Transit Time",
        value: currentJobSummary?.jsTransitTime ?? "0h",
      },
      {
        title: "Spent At Pickup",
        value: currentJobSummary?.jsSpentAtPickup ?? "0h",
      },
      {
        title: "Spent At Pickup Average",
        value: currentJobSummary?.jsSpentAtPickupAvg ?? "0h",
      },
      {
        title: "Spent At Pickup More Than 5 hours",
        value: currentJobSummary?.jsSpentAtPickupMtFiveh
          ? `${currentJobSummary?.jsSpentAtPickupMtFiveh} Jobs`
          : "0 Jobs",
      },
      {
        title: "On Time Booking",
        value: currentJobSummary?.jsOnTimeBooking
          ? `${currentJobSummary?.jsOnTimeBooking} Jobs`
          : "0 Jobs",
      },
      {
        title: "Confirmed Jobs",
        value: currentJobSummary?.jsConfirmedJobs
          ? `${currentJobSummary?.jsConfirmedJobs} Jobs`
          : "0 Jobs",
      },
      {
        title: "Disputed Jobs",
        value: currentJobSummary?.jsDisputedJobs
          ? `${currentJobSummary?.jsDisputedJobs} Jobs`
          : "0 Jobs",
      },
      {
        title: "Resolved Jobs",
        value: currentJobSummary?.jsResolvedJobs
          ? `${currentJobSummary?.jsResolvedJobs} Jobs`
          : "0 Jobs",
      },
      {
        title: "Spent At Delivery",
        value: currentJobSummary?.jsSpentAtDelivery ?? "0h",
      },
      {
        title: "Spent At Delivery Average",
        value: currentJobSummary?.jsSpentAtDeliveryAvg ?? "0h",
      },
      {
        title: "Spent At Delivery More Than 5 hours",
        value: currentJobSummary?.jsSpentAtDeliveryMtFiveh
          ? `${currentJobSummary?.jsSpentAtDeliveryMtFiveh} Jobs`
          : "0 Jobs",
      },
      {
        title: "On Time Placement For On Time Booking",
        value: currentJobSummary?.jsOtPlForOtBooking
          ? `${currentJobSummary?.jsOtPlForOtBooking} Jobs`
          : "0 Jobs",
      },
      {
        title: "Finance Confirmed Jobs",
        value: currentJobSummary?.jsFinComJobs
          ? `${currentJobSummary?.jsFinComJobs} Jobs`
          : "0 Jobs",
      },
      {
        title: "Finance Disputed Jobs",
        value: currentJobSummary?.jsFinDisJob
          ? `${currentJobSummary?.jsFinDisJob} Jobs`
          : "0 Jobs",
      },
      {
        title: "Finance Resolved Jobs",
        value: currentJobSummary?.jsFinResJob
          ? `${currentJobSummary?.jsFinResJob} Jobs`
          : "0 Jobs",
      },
      {
        title: "Total Transport Charge",
        value: currentJobSummary?.jsTransportCh
          ? formatter.format(currentJobSummary?.jsTransportCh)
          : formatter.format(0.0),
      },
      {
        title: "Total Demurrage Charge",
        value: currentJobSummary?.jsDemCh
          ? formatter.format(currentJobSummary?.jsDemCh)
          : formatter.format(0.0),
      },
      {
        title: "Total Night Allowance Charge",
        value: currentJobSummary?.jsNiAllCh
          ? formatter.format(currentJobSummary?.jsNiAllCh)
          : formatter.format(0.0),
      },
      {
        title: "Total Management Charge",
        value: currentJobSummary?.jsManagementCh
          ? formatter.format(currentJobSummary?.jsManagementCh)
          : formatter.format(0.0),
      },
      {
        title: "Total Other Charge",
        value: currentJobSummary?.jsOtherCh
          ? formatter.format(currentJobSummary?.jsOtherCh)
          : formatter.format(0.0),
      },
      {
        title: "Total Invoice Amount",
        value: currentJobSummary?.jsTotInvAmt
          ? formatter.format(currentJobSummary?.jsTotInvAmt)
          : formatter.format(0.0),
      },
      {
        title: "Total Credit Debit Amount",
        value: currentJobSummary?.jsCrDebAmt
          ? formatter.format(currentJobSummary?.jsCrDebAmt)
          : formatter.format(0.0),
      },
      {
        title: "Final Amount",
        value: currentJobSummary?.jsTotCh
          ? formatter.format(currentJobSummary?.jsTotCh)
          : formatter.format(0.0),
      },
    ];
    setJobSummary(jobInformation);
    setSelectedRows([]);
    setIsLoading(false);
    setActiveAccordion1(false);
  }, []);

  const handleBatchSearch = useCallback(async (data) => {
    setIsBatchSearch(true);
    setSearchClicked(true);
    setIsLoading(true);
    setBatchSearchParams(data);
    setCurrentSearchType(searchTypeConst.BATCH);
    const result = await searchBatchChargeConfirmation(data);
    setBatches(result);
    setSelectedRows([]);
    setIsLoading(false);
    setActiveAccordion2(false);
  }, []);

  useEffect(() => {
    if (updateNeeded) {
      if (currentSearchType === searchTypeConst.JOB) {
        handleSearch(searchParams);
      } else {
        handleBatchSearch(batchSearchParams);
      }
      setUpdateNeeded(false);
    }
  }, [
    updateNeeded,
    handleBatchSearch,
    handleSearch,
    currentSearchType,
    batchSearchParams,
    searchParams,
  ]);

  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
    setUpdateNeeded(true);
    setIsBatchSearch(false);
  };

  const getBatchFromJobs = (type) => {
    let currentBatchId = -1;

    for (let job of selectedRows) {
      let currentId = type === 1 ? job.invBatchid : job.evBatchid;
      if (currentBatchId === -1) {
        currentBatchId = currentId;
      } else {
        if (currentBatchId !== currentId) {
          return -2;
        }
      }
    }
    return currentBatchId;
  };

  const areAllJobsConfirmed = () => {
    for (let job of selectedRows) {
      if (job.jobConfStatus !== 3) {
        return false;
      }
    }

    return true;
  };

  const actionMenuHandler = (data) => {
    setIsBatchSearch(false);
    setEditMode(false);
    setCurrentBatchId(null);
    switch (data.value) {
      case 1: {
        const currentBatch = getBatchFromJobs(1);
        if (!areAllJobsConfirmed()) {
          toast.warn("All the Jobs should be confirmed!", {
            autoClose: 5000,
            containerId: "Main",
          });
          return;
        }
        if (currentBatch === -2) {
          toast.warn("Jobs from multiple batches selected!", {
            autoClose: 5000,
            containerId: "Main",
          });
          return;
        } else if (currentBatch) {
          setCurrentBatchId(currentBatch);
          setEditMode(true);
        }
        break;
      }
      case 2: {
        const currentBatch = getBatchFromJobs(2);
        if (currentBatch === -2) {
          toast.warn("Jobs from multiple batches selected!", {
            autoClose: 5000,
            containerId: "Main",
          });
          return;
        } else if (currentBatch) {
          setCurrentBatchId(currentBatch);
          setEditMode(true);
        }
        break;
      }
      default: {
        toast.error("Invalid Action", { autoClose: 5000, containerId: "Main" });
        return;
      }
    }
    setStkType(data.value);
    setIsDrawerOpen(true);
  };

  const handleSelectionChanged = useCallback(() => {
    const selectedData = gridRef.current.getSelectedData();
    setSelectedRows(selectedData);
  }, []);

  const cellDoubleClickHandler = (params) => {
    setIsBatchSearch(true);
    setCurrentBatchId(params.data.inhid);
    setStkType(params.data.instype);
    setEditMode(true);
    setIsDrawerOpen(true);
  };

  const getActionMenuOptions = () => {
    const opt = [];

    if (invPermissions) {
      opt.push({
        key: 1,
        text: "Customer Approval",
        value: 1,
      });
    }

    if (evPermissions) {
      opt.push({
        key: 2,
        text: "Transporter Approval",
        value: 2,
      });
    }

    return opt;
  };

  return (
    <>
      <div className="ui tabular menu top attached">
        {!pendingSearchPermission && !approvedSearchPermission ? (
          <div
            style={{
              height: "80vh",
              width: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <LoadingSpinner
              spinner="no-permission"
              message="You dont have sufficient permissions"
            />
          </div>
        ) : (
          <>
            {pendingSearchPermission && (
              <Tab
                Icon={IoCheckmarkCircleOutline}
                onClick={() => {
                  setTab(1);
                }}
                active={tab === 1}
                label="Approval"
              />
            )}
            {approvedSearchPermission && (
              <Tab
                Icon={Search}
                onClick={() => {
                  setTab(2);
                }}
                active={tab === 2}
                label="Search"
              />
            )}
          </>
        )}
      </div>
      <div
        className="ui bottom attached active tab segment"
        style={{ display: tab === 1 ? "block" : "none" }}
      >
        <div className="content active">
          <ChargeConfirmationSearch
            searchResHandler={handleSearch}
            resetHandler={() => {}}
            activeAccordion1={activeAccordion1}
            setActiveAccordion1={setActiveAccordion1}
            invStatusPermission={invStatusPermission}
            evStatusPermission={evStatusPermission}
          />
          {searchClicked && (
            <>
              {isLoading ? (
                <LoadingSpinner />
              ) : (
                <>
                  <Grid
                    ref={gridRef}
                    wrapText={false}
                    heading={`Pick the jobs for charge approval (${jobCount} Jobs) `}
                    multiRowSelection={true}
                    onSelectionChanged={handleSelectionChanged}
                    showActionMenu={selectedRows.length > 0}
                    actionMenuOptions={getActionMenuOptions()}
                    handleActionMenuChange={actionMenuHandler}
                    isLoading={isLoading}
                    module={MODULES.Finance}
                    searchable={true}
                    config={[
                      {
                        headerName: "Charge",
                        field: "chgCode",
                        headerCheckboxSelection: true,
                        checkboxSelection: true,
                        pinned: "left",
                        minWidth: 170,
                        width: 170,
                        filter: true,
                        module: MODULES.Finance,
                        primaryForm: HEADER_FORM,
                        authenticated: true,
                      },
                      {
                        headerName: "Job",
                        field: "jobCode",
                        pinned: "left",
                        minWidth: 150,
                        width: 150,
                        filter: true,
                        module: MODULES.Finance,
                        primaryForm: HEADER_FORM,
                        authenticated: true,
                      },
                      {
                        headerName: "Request",
                        field: "requestCode",
                        pinned: "left",
                        minWidth: 150,
                        width: 150,
                        filter: true,
                        module: MODULES.Finance,
                        primaryForm: HEADER_FORM,
                        authenticated: true,
                      },
                      {
                        headerName: "Customer",
                        field: "customer",
                        cellRenderer: ResizableScrollingTextRenderer,
                        module: MODULES.Finance,
                        primaryForm: HEADER_FORM,
                        authenticated: true,
                      },
                      {
                        headerName: "Shipper",
                        field: "shipper",
                        cellRenderer: ResizableScrollingTextRenderer,
                        module: MODULES.Finance,
                        primaryForm: HEADER_FORM,
                        authenticated: true,
                      },
                      {
                        headerName: "Transporter",
                        field: "transporter",
                        cellRenderer: ResizableScrollingTextRenderer,
                        module: MODULES.Finance,
                        primaryForm: HEADER_FORM,
                        authenticated: true,
                      },
                      {
                        headerName: "Created By",
                        field: "creator",
                        cellRenderer: ResizableScrollingTextRenderer,
                        module: MODULES.Finance,
                        primaryForm: HEADER_FORM,
                        authenticated: true,
                      },
                      {
                        headerName: "Consignment Type",
                        field: "consType",
                        cellRenderer: ResizableScrollingTextRenderer,
                        module: MODULES.Finance,
                        primaryForm: HEADER_FORM,
                        authenticated: true,
                      },
                      {
                        headerName: "Consignee",
                        field: "consignee",
                        module: MODULES.Finance,
                        primaryForm: HEADER_FORM,
                        authenticated: true,
                      },
                      {
                        headerName: "Vehicle",
                        field: "vehNo",
                        module: MODULES.Finance,
                        primaryForm: HEADER_FORM,
                        authenticated: true,
                      },
                      {
                        headerName: "Start Location",
                        field: "startLoc",
                        module: MODULES.Finance,
                        cellRenderer: ResizableScrollingTextRenderer,
                        primaryForm: HEADER_FORM,
                        authenticated: true,
                      },
                      {
                        headerName: "End Location",
                        field: "endLoc",
                        cellRenderer: ResizableScrollingTextRenderer,
                        module: MODULES.Finance,
                        primaryForm: HEADER_FORM,
                        authenticated: true,
                      },
                      {
                        headerName: "Distance",
                        field: "distance",
                        module: MODULES.Finance,
                        primaryForm: HEADER_FORM,
                        authenticated: true,
                      },
                      {
                        headerName: "EV Status",
                        field: "evsstatusname",
                        cellRenderer: EVInvCellStyle,
                        module: MODULES.Finance,
                        primaryForm: HEADER_FORM,
                        authenticated: true,
                      },
                      {
                        headerName: "EV Batch Number",
                        field: "evBatchno",
                        module: MODULES.Finance,
                        primaryForm: HEADER_FORM,
                        authenticated: true,
                      },
                      {
                        headerName: "INV Status",
                        field: "invstatusname",
                        cellRenderer: EVInvCellStyle,
                        module: MODULES.Finance,
                        primaryForm: HEADER_FORM,
                        authenticated: true,
                      },
                      {
                        headerName: "INV Batch Number",
                        field: "invBatchno",
                        module: MODULES.Finance,
                        primaryForm: HEADER_FORM,
                        authenticated: true,
                      },
                      {
                        headerName: "Additional Charge",
                        field: "chgAdditionalValue",
                        module: MODULES.Finance,
                        primaryForm: HEADER_FORM,
                        authenticated: true,
                      },
                    ]}
                    content={jobs}
                  />
                  <InfoList
                    content={jobSummary}
                    title="Finance Confirmation Summary"
                  />
                </>
              )}
            </>
          )}
        </div>
      </div>
      <div
        className="ui bottom attached active tab segment"
        style={{ display: tab === 2 ? "block" : "none" }}
      >
        <div className="content active">
          <BatchChargeSearch
            searchResHandler={handleBatchSearch}
            resetHandler={() => {}}
            activeAccordion2={activeAccordion2}
            setActiveAccordion2={setActiveAccordion2}
          />
          {searchClicked && (
            <>
              {isLoading ? (
                <LoadingSpinner />
              ) : (
                <>
                  <Grid
                    ref={batchGridRef}
                    wrapText={false}
                    onCellDoubleClicked={cellDoubleClickHandler}
                    isLoading={isLoading}
                    module={MODULES.Finance}
                    searchable={true}
                    config={[
                      {
                        headerName: "Batch",
                        field: "inhcode",
                        headerCheckboxSelection: true,
                        checkboxSelection: true,
                        pinned: "left",
                        minWidth: 150,
                        width: 150,
                        filter: true,
                      },
                      {
                        headerName: "Customer",
                        field: "customers",
                        cellRenderer: ResizableScrollingTextRenderer,
                      },
                      {
                        headerName: "Shipper",
                        field: "shippers",
                        cellRenderer: ResizableScrollingTextRenderer,
                      },
                      {
                        headerName: "Transporter",
                        field: "transporters",
                        cellRenderer: ResizableScrollingTextRenderer,
                        module: MODULES.Finance,
                        primaryForm: HEADER_FORM,
                        authenticated: true,
                      },
                      {
                        headerName: "Job",
                        field: "jobs",
                        cellRenderer: ResizableScrollingTextRenderer,
                      },
                      {
                        headerName: "Vehicle",
                        field: "vehicles",
                        cellRenderer: ResizableScrollingTextRenderer,
                      },
                      { headerName: "Type", field: "instypename" },
                    ]}
                    content={batches}
                  />
                </>
              )}
            </>
          )}
        </div>
      </div>

      <SideDrawer
        open={isDrawerOpen}
        handleDrawerClose={handleDrawerClose}
        head={
          <h3 className="ui header">
            {stkType === 1 ? "Customer Approval" : "Transporter Approval"}
          </h3>
        }
        body={
          <CustomerTransporterChargeApproval
            isBatchSearch={isBatchSearch}
            type={stkType}
            charges={selectedRows}
            isEditMode={editMode}
            batchId={currentBatchId}
          />
        }
      />
    </>
  );
};

export default ChargeConfirmation;
